// src/render/canvas.ts
// Canvas renderer — "Block Out"-style look: a dark purple Lego baseplate packed with vibrant glossy
// studded bricks (color only), with small colored exit chevrons on the perimeter for gates. Selection
// lifts a brick; the hint glows + nudges it. HiDPI aware + responsive. Pure drawing.

import type { Block } from '../game/block';
import type { GameState } from '../game/rules';
import type { BlockId, Cell, Color, Direction, Side } from '../game/types';
import { blockBounds, blockCells, isCovered } from '../game/block';
import { roundRectPath } from './symbols';

// ---- Render model ----------------------------------------------------------

export interface BlockAnim {
  readonly blockId: BlockId;
  readonly offsetRow: number;
  readonly offsetCol: number;
  readonly alpha: number;
  readonly scale: number;
}

export interface RenderModel {
  readonly state: GameState;
  readonly selectedBlockId: BlockId | null;
  readonly focusVisible: boolean;
  readonly anim: BlockAnim | null;
  readonly hint: { readonly blockId: BlockId; readonly dir: Direction } | null;
  readonly hintPulse: number;
}

// ---- Palette (vibrant, tuned to read on a dark baseplate) -------------------

// Maximally-distinct, vibrant hues spread around the wheel so matching a brick to its exit chevron
// is unambiguous (the whole game is colour matching). Red is kept pink-leaning so it never reads
// as orange; purple is violet so it never reads as blue.
const COLOR_HEX: Readonly<Record<Color, string>> = {
  red: '#FF3D63',
  blue: '#2E8BFF',
  green: '#2FCB57',
  yellow: '#FFD21F',
  purple: '#B24DFF',
  orange: '#FF8A1E',
};

const PANEL_TOP = '#332B68';
const PANEL_BOT = '#231C4E';
const BASEPLATE = '#2A2358';
const STUD_LIGHT = 'rgba(255, 255, 255, 0.06)';
const STUD_DARK = 'rgba(0, 0, 0, 0.18)';
const FOCUS_RING = '#FFFFFF';

/** Wall thickness as a fraction of one cell (room for the exit chevrons). */
const FRAME_RATIO = 0.46;

// ---- Geometry --------------------------------------------------------------

interface Metrics {
  readonly cssW: number;
  readonly cssH: number;
  readonly rows: number;
  readonly cols: number;
  readonly cell: number;
  readonly frame: number;
  readonly boardX: number;
  readonly boardY: number;
  readonly outerX: number;
  readonly outerY: number;
  readonly outerW: number;
  readonly outerH: number;
}

const DELTA: Readonly<Record<Direction, readonly [number, number]>> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

// ---- Brick shading helpers (procedural 2.5D, mirrors scripts/gen-bricks.cjs) ----

function parseHex(hex: string): readonly [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function toHex(r: number, g: number, b: number): string {
  const cl = (v: number): string =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${cl(r)}${cl(g)}${cl(b)}`;
}

/** Blend `hex` toward white by `t` (0..1). */
function lighten(hex: string, t: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t);
}

/** Blend `hex` toward black by `t` (0..1). */
function darken(hex: string, t: number): string {
  const [r, g, b] = parseHex(hex);
  return toHex(r * (1 - t), g * (1 - t), b * (1 - t));
}

// ---- Pure draw-order helper (exported for unit testing) --------------------

/**
 * Stable draw order: the animated/sliding block draws last (on top); otherwise
 * top rows first then left cols, using each block's bounding-box min corner.
 * Returns a new array; does not mutate the input.
 */
export function sortForDraw(blocks: readonly Block[], animId: BlockId | null): readonly Block[] {
  return [...blocks].sort((a, b) => {
    const aa = animId === a.id ? 1 : 0;
    const ab = animId === b.id ? 1 : 0;
    if (aa !== ab) return aa - ab;
    const ra = blockBounds(a).min.row;
    const rb = blockBounds(b).min.row;
    if (ra !== rb) return ra - rb;
    return blockBounds(a).min.col - blockBounds(b).min.col;
  });
}

// ---- Canvas view -----------------------------------------------------------

export class CanvasView {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly ro: ResizeObserver | null;
  private readonly onWindowResize: () => void;
  private metrics: Metrics | null = null;
  private lastModel: RenderModel | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (ctx === null) {
      throw new Error('CanvasView: 2D context unavailable');
    }
    this.ctx = ctx;

    this.onWindowResize = () => {
      if (this.lastModel !== null) {
        this.render(this.lastModel);
      }
    };
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => {
        if (this.lastModel !== null) {
          this.render(this.lastModel);
        }
      });
      this.ro.observe(canvas);
    } else {
      this.ro = null;
    }
    window.addEventListener('resize', this.onWindowResize);
  }

  dispose(): void {
    if (this.ro !== null) {
      this.ro.disconnect();
    }
    window.removeEventListener('resize', this.onWindowResize);
  }

  get cellPx(): number {
    return this.metrics === null ? 0 : this.metrics.cell;
  }

  clientToCell(clientX: number, clientY: number): Cell | null {
    const m = this.metrics;
    if (m === null) {
      return null;
    }
    const rect = this.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const col = Math.floor((px - m.boardX) / m.cell);
    const row = Math.floor((py - m.boardY) / m.cell);
    if (row < 0 || row >= m.rows || col < 0 || col >= m.cols) {
      return null;
    }
    return { row, col };
  }

  /**
   * Fractional, UNCLAMPED cell coordinates of a client point (board col 0 / row 0 origin at 0,0).
   * Values can be negative or exceed the grid — used by free-roam dragging to find the nearest
   * reachable anchor and to detect a drag pushed off-board toward an exit gate. Null before layout.
   */
  clientToCellF(clientX: number, clientY: number): { readonly row: number; readonly col: number } | null {
    const m = this.metrics;
    if (m === null) {
      return null;
    }
    const rect = this.canvas.getBoundingClientRect();
    return {
      row: (clientY - rect.top - m.boardY) / m.cell,
      col: (clientX - rect.left - m.boardX) / m.cell,
    };
  }

  // ---- rendering ----

  render(model: RenderModel): void {
    this.lastModel = model;
    const m = this.computeMetrics(model.state);
    if (m === null) {
      return;
    }
    this.metrics = m;

    const ctx = this.ctx;
    ctx.clearRect(0, 0, m.cssW, m.cssH); // transparent — the page gradient shows around the board

    this.drawPanel(m);
    this.drawBaseplate(m);
    this.drawGates(m, model);
    this.drawBricks(m, model);
  }

  private computeMetrics(state: GameState): Metrics | null {
    const cssW = this.canvas.clientWidth;
    const cssH = this.canvas.clientHeight;
    if (cssW <= 0 || cssH <= 0) {
      return null;
    }
    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    const pxW = Math.round(cssW * dpr);
    const pxH = Math.round(cssH * dpr);
    if (this.canvas.width !== pxW) {
      this.canvas.width = pxW;
    }
    if (this.canvas.height !== pxH) {
      this.canvas.height = pxH;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const { rows, cols } = state.board;
    const unitsW = cols + 2 * FRAME_RATIO;
    const unitsH = rows + 2 * FRAME_RATIO;
    const pad = 6;
    const cell = Math.floor(Math.min((cssW - pad) / unitsW, (cssH - pad) / unitsH));
    const safeCell = Math.max(8, cell);
    const frame = safeCell * FRAME_RATIO;
    const drawnW = cols * safeCell + 2 * frame;
    const drawnH = rows * safeCell + 2 * frame;
    const outerX = (cssW - drawnW) / 2;
    const outerY = (cssH - drawnH) / 2;
    return {
      cssW,
      cssH,
      rows,
      cols,
      cell: safeCell,
      frame,
      boardX: outerX + frame,
      boardY: outerY + frame,
      outerX,
      outerY,
      outerW: drawnW,
      outerH: drawnH,
    };
  }

  /** The dark board panel (deep purple), floating over the page with a drop shadow + rim light. */
  private drawPanel(m: Metrics): void {
    const ctx = this.ctx;
    const r = m.frame * 0.95;

    ctx.save();
    ctx.shadowColor = 'rgba(10, 6, 30, 0.5)';
    ctx.shadowBlur = m.frame * 1.5;
    ctx.shadowOffsetY = m.frame * 0.5;
    const g = ctx.createLinearGradient(0, m.outerY, 0, m.outerY + m.outerH);
    g.addColorStop(0, PANEL_TOP);
    g.addColorStop(1, PANEL_BOT);
    ctx.fillStyle = g;
    roundRectPath(ctx, m.outerX, m.outerY, m.outerW, m.outerH, r);
    ctx.fill();
    ctx.restore();

    // Top rim light.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = Math.max(1, m.cell * 0.02);
    roundRectPath(ctx, m.outerX, m.outerY, m.outerW, m.outerH, r);
    ctx.stroke();
  }

  /** One continuous, flat, recessed Lego baseplate with a subtle uniform stud grid (NOT per-cell tiles). */
  private drawBaseplate(m: Metrics): void {
    const ctx = this.ctx;
    const fx = m.boardX;
    const fy = m.boardY;
    const fw = m.cols * m.cell;
    const fh = m.rows * m.cell;
    const fr = m.cell * 0.14;

    // Flat recessed field surface.
    ctx.fillStyle = BASEPLATE;
    roundRectPath(ctx, fx, fy, fw, fh, fr);
    ctx.fill();

    ctx.save();
    roundRectPath(ctx, fx, fy, fw, fh, fr);
    ctx.clip();

    // Inner top shadow → the field sits recessed below the frame.
    const sh = ctx.createLinearGradient(0, fy, 0, fy + m.cell * 0.5);
    sh.addColorStop(0, 'rgba(0,0,0,0.3)');
    sh.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = sh;
    ctx.fillRect(fx, fy, fw, m.cell * 0.5);

    // Subtle uniform stud grid (2 per cell, brick-stud pitch) across the whole surface.
    const sr = m.cell * 0.07;
    const off = m.cell * 0.225;
    for (let row = 0; row < m.rows; row++) {
      for (let col = 0; col < m.cols; col++) {
        const ccx = m.boardX + (col + 0.5) * m.cell;
        const ccy = m.boardY + (row + 0.5) * m.cell;
        for (const [ox, oy] of STUD_OFFSETS) {
          const sx = ccx + ox * off;
          const sy = ccy + oy * off;
          ctx.beginPath();
          ctx.fillStyle = STUD_DARK;
          ctx.arc(sx, sy + sr * 0.45, sr, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.fillStyle = STUD_LIGHT;
          ctx.arc(sx, sy, sr, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.restore();
  }

  /**
   * Gates as FULL-WIDTH colored bars embedded in the frame, spanning the opening, each with a small
   * arrow pointing the way a matching brick leaves (matches the reference art).
   */
  private drawGates(m: Metrics, model: RenderModel): void {
    const ctx = this.ctx;
    for (const gate of model.state.board.gates) {
      const hex = COLOR_HEX[gate.color];
      const dir = SIDE_DIR[gate.side];
      const vertical = gate.side === 'left' || gate.side === 'right';
      const rect = this.gateBarRect(m, gate);
      const pad = m.frame * 0.13;
      const bx = rect.x + pad;
      const by = rect.y + pad;
      const bw = rect.w - pad * 2;
      const bh = rect.h - pad * 2;
      const radius = Math.min(bw, bh) * 0.32;

      // Colored bar with a soft toy-plastic gradient + drop shadow.
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.32)';
      ctx.shadowBlur = m.cell * 0.08;
      ctx.shadowOffsetY = m.cell * 0.03;
      const grad = vertical
        ? ctx.createLinearGradient(bx, 0, bx + bw, 0)
        : ctx.createLinearGradient(0, by, 0, by + bh);
      grad.addColorStop(0, lighten(hex, 0.14));
      grad.addColorStop(1, darken(hex, 0.14));
      ctx.fillStyle = grad;
      roundRectPath(ctx, bx, by, bw, bh, radius);
      ctx.fill();
      ctx.restore();

      // Soft top highlight on the bar.
      ctx.save();
      roundRectPath(ctx, bx, by, bw, bh, radius);
      ctx.clip();
      ctx.fillStyle = 'rgba(255,255,255,0.16)';
      if (vertical) {
        ctx.fillRect(bx, by, bw * 0.4, bh);
      } else {
        ctx.fillRect(bx, by, bw, bh * 0.4);
      }
      ctx.restore();

      // White arrow centered on the bar, with a soft dark halo so it pops on any bar color.
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      const a = Math.min(bw, bh) * 0.32;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = m.cell * 0.05;
      ctx.fillStyle = 'rgba(255,255,255,0.96)';
      this.chevronWide(cx, cy, a, a * 0.92, dir);
      ctx.fill();
      ctx.restore();
    }
  }

  /** The frame-band rectangle a gate fills: full span along its edge × the frame thickness. */
  private gateBarRect(
    m: Metrics, gate: { side: Side; line: number; span: number },
  ): { x: number; y: number; w: number; h: number } {
    switch (gate.side) {
      case 'top':
        return {
          x: m.boardX + gate.line * m.cell, y: m.outerY,
          w: gate.span * m.cell, h: m.boardY - m.outerY,
        };
      case 'bottom': {
        const by = m.boardY + m.rows * m.cell;
        return {
          x: m.boardX + gate.line * m.cell, y: by,
          w: gate.span * m.cell, h: m.outerY + m.outerH - by,
        };
      }
      case 'left':
        return {
          x: m.outerX, y: m.boardY + gate.line * m.cell,
          w: m.boardX - m.outerX, h: gate.span * m.cell,
        };
      case 'right': {
        const rx = m.boardX + m.cols * m.cell;
        return {
          x: rx, y: m.boardY + gate.line * m.cell,
          w: m.outerX + m.outerW - rx, h: gate.span * m.cell,
        };
      }
      default: {
        const _exhaustive: never = gate.side;
        return _exhaustive;
      }
    }
  }

  /**
   * Filled chevron pointing `dir`, centered at (cx, cy).
   * `depth` = tip-to-base extent along the pointing axis.
   * `halfWidth` = half-width of the base perpendicular to the pointing axis.
   */
  private chevronWide(
    cx: number, cy: number, depth: number, halfWidth: number, dir: Direction,
  ): void {
    const ctx = this.ctx;
    const a = depth;
    const b = halfWidth;
    ctx.beginPath();
    switch (dir) {
      case 'up':
        ctx.moveTo(cx, cy - a);
        ctx.lineTo(cx + b, cy + a * 0.6);
        ctx.lineTo(cx - b, cy + a * 0.6);
        break;
      case 'down':
        ctx.moveTo(cx, cy + a);
        ctx.lineTo(cx + b, cy - a * 0.6);
        ctx.lineTo(cx - b, cy - a * 0.6);
        break;
      case 'left':
        ctx.moveTo(cx - a, cy);
        ctx.lineTo(cx + a * 0.6, cy + b);
        ctx.lineTo(cx + a * 0.6, cy - b);
        break;
      case 'right':
        ctx.moveTo(cx + a, cy);
        ctx.lineTo(cx - a * 0.6, cy + b);
        ctx.lineTo(cx - a * 0.6, cy - b);
        break;
      default: {
        const _exhaustive: never = dir;
        return _exhaustive;
      }
    }
    ctx.closePath();
  }

  private drawBricks(m: Metrics, model: RenderModel): void {
    const blocks = sortForDraw(model.state.blocks, model.anim?.blockId ?? null);
    for (const block of blocks) {
      this.drawBrick(m, model, block);
    }
  }

  private drawBrick(m: Metrics, model: RenderModel, block: Block): void {
    const ctx = this.ctx;
    const bounds = blockBounds(block);
    let bx = m.boardX + bounds.min.col * m.cell;
    let by = m.boardY + bounds.min.row * m.cell;
    const bw = (bounds.max.col - bounds.min.col + 1) * m.cell;
    const bh = (bounds.max.row - bounds.min.row + 1) * m.cell;

    const anim = model.anim !== null && model.anim.blockId === block.id ? model.anim : null;
    let alpha = 1;
    let scale = 1;
    if (anim !== null) {
      bx += anim.offsetCol * m.cell;
      by += anim.offsetRow * m.cell;
      alpha = anim.alpha;
      scale = anim.scale;
    }

    const selected = model.selectedBlockId === block.id;
    const hinted = model.hint !== null && model.hint.blockId === block.id;
    const pulse = Math.max(0, Math.min(1, model.hintPulse));
    if (hinted && model.hint !== null) {
      const [dx, dy] = DELTA[model.hint.dir];
      bx += dx * m.cell * 0.1 * pulse;
      by += dy * m.cell * 0.1 * pulse;
    }
    if (selected) {
      scale *= 1.05;
    }

    ctx.save();
    ctx.globalAlpha = alpha;
    if (scale !== 1) {
      const cx = bx + bw / 2;
      const cy = by + bh / 2;
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
    }

    if (isCovered(block)) {
      // Hay overlay over bounding box (spec: ok to use bounding box for covered pieces).
      const pad = m.cell * 0.05;
      ctx.save();
      ctx.shadowColor = 'rgba(8, 4, 24, 0.5)';
      ctx.shadowBlur = m.cell * 0.14;
      ctx.shadowOffsetY = m.cell * 0.09;
      this.fillHay(bx + pad, by + pad, bw - pad * 2, bh - pad * 2, m.cell * 0.18);
      ctx.restore();
    } else {
      // Pulsing colour glow behind a hinted brick (over bounding box, per task spec).
      if (hinted) {
        ctx.save();
        ctx.shadowColor = COLOR_HEX[block.color];
        ctx.shadowBlur = m.cell * (0.3 + 0.2 * pulse);
        ctx.fillStyle = COLOR_HEX[block.color];
        roundRectPath(ctx, bx + m.cell * 0.12, by + m.cell * 0.12, bw - m.cell * 0.24, bh - m.cell * 0.24, m.cell * 0.16);
        ctx.fill();
        ctx.restore();
      }

      // Unified 2.5D brick: every piece (1×1 or polyomino) renders as one connected, studded,
      // extruded Lego brick following its silhouette. originX/Y = board origin + anim offset + hint
      // nudge (absolute px for cell.col/row = 0).
      const originX = bx - bounds.min.col * m.cell;
      const originY = by - bounds.min.row * m.cell;
      this.drawBrickBody(m, block, originX, originY);
    }

    // Focus ring (keyboard selection) over bounding box — works for any shape.
    if (selected && model.focusVisible) {
      const o = m.cell * 0.05;
      ctx.strokeStyle = FOCUS_RING;
      ctx.lineWidth = Math.max(2, m.cell * 0.04);
      ctx.setLineDash([m.cell * 0.16, m.cell * 0.11]);
      roundRectPath(ctx, bx - o, by - o, bw + o * 2, bh + o * 2, m.cell * 0.18 + o);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }

  /**
   * Draw any piece (1×1 or polyomino) as a single connected 2.5D Lego brick:
   * a soft drop shadow, an extruded side wall following the silhouette, a rounded top face with a
   * seam outline + top bevel highlight, and flat-topped cylinder studs (2×2 per cell). Only the
   * silhouette's truly exposed outer corners are rounded; shared internal edges join flush.
   *
   * `originX/Y` = CSS pixel position where board column 0 / row 0 would be, already incorporating
   * any animation offset and hint nudge (i.e. `bx - bounds.min.col * m.cell`).
   */
  private drawBrickBody(m: Metrics, block: Block, originX: number, originY: number): void {
    const ctx = this.ctx;
    const hex = COLOR_HEX[block.color];
    const cell = m.cell;
    const cells = blockCells(block);
    const occ = new Set(cells.map((c) => `${c.row},${c.col}`));
    const has = (r: number, c: number): boolean => occ.has(`${r},${c}`);

    const radius = cell * 0.17; // softly rounded toy-brick corners
    const eps = cell * 0.03; // overlap on shared edges (kills internal seams)

    let topY = Infinity;
    let botY = -Infinity;
    for (const c of cells) {
      topY = Math.min(topY, originY + c.row * cell);
      botY = Math.max(botY, originY + (c.row + 1) * cell);
    }

    // Trace the silhouette into the current path. Each cell extends `eps` into any occupied
    // neighbour so adjacent fills overlap (no internal seam); a corner rounds only when both of the
    // edges meeting there are exposed.
    const trace = (): void => {
      ctx.beginPath();
      for (const c of cells) {
        const l = has(c.row, c.col - 1);
        const r = has(c.row, c.col + 1);
        const u = has(c.row - 1, c.col);
        const d = has(c.row + 1, c.col);
        const x0 = originX + c.col * cell + (l ? -eps : 0);
        const x1 = originX + (c.col + 1) * cell + (r ? eps : 0);
        const y0 = originY + c.row * cell + (u ? -eps : 0);
        const y1 = originY + (c.row + 1) * cell + (d ? eps : 0);
        this.cellCornerPath(
          x0, y0, x1 - x0, y1 - y0,
          !u && !l ? radius : 0,
          !u && !r ? radius : 0,
          !d && !r ? radius : 0,
          !d && !l ? radius : 0,
        );
      }
    };

    // 1) Matte top face, viewed straight down. A soft contact shadow lifts the piece off the plate.
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = cell * 0.08;
    ctx.shadowOffsetY = cell * 0.06;
    const face = ctx.createLinearGradient(0, topY, 0, botY);
    face.addColorStop(0, lighten(hex, 0.07));
    face.addColorStop(1, darken(hex, 0.07));
    ctx.fillStyle = face;
    trace();
    ctx.fill();
    ctx.restore();

    // 2) Thin darker outline for definition.
    ctx.strokeStyle = darken(hex, 0.24);
    ctx.lineWidth = Math.max(1, cell * 0.03);
    trace();
    ctx.stroke();

    // 3) Light, rounded TOP (and left) edge — the brick's upper rim catches the light. Matte + soft.
    ctx.strokeStyle = lighten(hex, 0.34);
    ctx.lineWidth = Math.max(1.5, cell * 0.05);
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.7;
    ctx.beginPath();
    for (const c of cells) {
      if (!has(c.row - 1, c.col)) {
        const y = originY + c.row * cell + cell * 0.085;
        const xl = originX + c.col * cell + (has(c.row, c.col - 1) ? 0 : cell * 0.18);
        const xr = originX + (c.col + 1) * cell - (has(c.row, c.col + 1) ? 0 : cell * 0.18);
        ctx.moveTo(xl, y);
        ctx.lineTo(xr, y);
      }
      if (!has(c.row, c.col - 1)) {
        const x = originX + c.col * cell + cell * 0.085;
        const yt = originY + c.row * cell + (has(c.row - 1, c.col) ? 0 : cell * 0.18);
        const yb = originY + (c.row + 1) * cell - (has(c.row + 1, c.col) ? 0 : cell * 0.18);
        ctx.moveTo(x, yt);
        ctx.lineTo(x, yb);
      }
    }
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.lineCap = 'butt';

    // 4) Very faint internal seams so the piece's unit structure barely reads.
    ctx.strokeStyle = darken(hex, 0.1);
    ctx.lineWidth = Math.max(1, cell * 0.015);
    ctx.beginPath();
    for (const c of cells) {
      if (has(c.row, c.col + 1)) {
        const x = originX + (c.col + 1) * cell;
        ctx.moveTo(x, originY + c.row * cell + cell * 0.16);
        ctx.lineTo(x, originY + (c.row + 1) * cell - cell * 0.16);
      }
      if (has(c.row + 1, c.col)) {
        const y = originY + (c.row + 1) * cell;
        ctx.moveTo(originX + c.col * cell + cell * 0.16, y);
        ctx.lineTo(originX + (c.col + 1) * cell - cell * 0.16, y);
      }
    }
    ctx.stroke();

    // 5) Studs — matte plastic POSTS seen top-down: a soft shadow around each post base and a small,
    // slight sheen on top (NOT a glossy dome). Light from the upper-left.
    const off = cell * 0.22;
    const sr = cell * 0.155;
    for (const c of cells) {
      const ccx = originX + (c.col + 0.5) * cell;
      const ccy = originY + (c.row + 0.5) * cell;
      for (const [ox, oy] of STUD_OFFSETS) {
        const sx = ccx + ox * off;
        const sy = ccy + oy * off;
        // post body with a soft base shadow (post sits proud, casts a little shadow on the brick)
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.32)';
        ctx.shadowBlur = sr * 0.4;
        ctx.shadowOffsetY = sr * 0.22;
        const body = ctx.createRadialGradient(
          sx - sr * 0.2, sy - sr * 0.25, sr * 0.12,
          sx, sy, sr,
        );
        body.addColorStop(0, lighten(hex, 0.12));
        body.addColorStop(0.7, hex);
        body.addColorStop(1, darken(hex, 0.1));
        ctx.beginPath();
        ctx.fillStyle = body;
        ctx.arc(sx, sy, sr, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        // small, slight sheen on the upper-left of the post (subtle — matte, not glossy)
        const sheen = ctx.createRadialGradient(
          sx - sr * 0.28, sy - sr * 0.32, 0,
          sx - sr * 0.28, sy - sr * 0.32, sr * 0.7,
        );
        sheen.addColorStop(0, 'rgba(255, 255, 255, 0.28)');
        sheen.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.beginPath();
        ctx.fillStyle = sheen;
        ctx.arc(sx, sy, sr, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /** Add one cell's rounded-rect subpath to the current path (per-corner radii; 0 ⇒ square join). */
  private cellCornerPath(
    x: number, y: number, w: number, h: number,
    rTL: number, rTR: number, rBR: number, rBL: number,
  ): void {
    const ctx = this.ctx;
    ctx.moveTo(x + rTL, y);
    ctx.lineTo(x + w - rTR, y);
    if (rTR) ctx.arcTo(x + w, y, x + w, y + rTR, rTR);
    ctx.lineTo(x + w, y + h - rBR);
    if (rBR) ctx.arcTo(x + w, y + h, x + w - rBR, y + h, rBR);
    ctx.lineTo(x + rBL, y + h);
    if (rBL) ctx.arcTo(x, y + h, x, y + h - rBL, rBL);
    ctx.lineTo(x, y + rTL);
    if (rTL) ctx.arcTo(x, y, x + rTL, y, rTL);
    ctx.closePath();
  }

  private fillHay(bx: number, by: number, bw: number, bh: number, radius: number): void {
    const ctx = this.ctx;
    const grad = ctx.createLinearGradient(bx, by, bx, by + bh);
    grad.addColorStop(0, '#D8B879');
    grad.addColorStop(1, '#B8924F');
    ctx.fillStyle = grad;
    roundRectPath(ctx, bx, by, bw, bh, radius);
    ctx.fill();
    ctx.save();
    roundRectPath(ctx, bx, by, bw, bh, radius);
    ctx.clip();
    ctx.strokeStyle = 'rgba(90, 62, 20, 0.5)';
    ctx.lineWidth = Math.max(1, bw * 0.04);
    const stepPx = Math.max(6, bw * 0.2);
    for (let i = -bh; i < bw + bh; i += stepPx) {
      ctx.beginPath();
      ctx.moveTo(bx + i, by);
      ctx.lineTo(bx + i + bh, by + bh);
      ctx.stroke();
    }
    ctx.restore();
  }
}

/** 2x2 stud offsets (unit corners). */
const STUD_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [1, 1],
];

const SIDE_DIR: Readonly<Record<Side, Direction>> = {
  top: 'up',
  bottom: 'down',
  left: 'left',
  right: 'right',
};
